<template>
  <div class="gallery flex items-start">
    <div class="relative" style="flex: 0 0 80px; height: 260px">

      <swiper :free-mode="true" :modules="[SwiperNavigation, SwiperThumbs]"
              :navigation="{ nextEl: nextRef, prevEl: prevRef }" :slidesPerView="3" :space-between="10"
              class="gallery-thumb" direction="vertical" @swiper="setThumbsSwiper">

        <div ref="prevRef" class="flex-center cursor-pointer absolute left-0 right-0 top-0 bg-white opacity-90 z-10">
          <van-icon name="arrow-up" size="24"></van-icon>
        </div>
        <div ref="nextRef" class="flex-center cursor-pointer absolute left-0 right-0 bottom-0 bg-white opacity-90 z-10">
          <van-icon name="arrow-down" size="24"></van-icon>
        </div>

        <swiper-slide v-for="(image, index) in combineImages" :key="`gallery-thumb-${index}`" :style="getInitialStyle">
          <div class="relative">

            <!-- Gallery Thumbnails placeholders -->
            <div :class="{ 'video-play-tag': index >= images.length && !!data?.video_url }"></div>
            <div :class="{ 'image-comparison': index >= images.length && !!beforeAfterImage }"></div>
            <div :class="{ 'three-sixty-preview': index >= images.length && !!threeSixtyImages.length }"></div>

            <niceone-image :alt="`gallery-thumb-${index}`" :src="image" class="p-1 rounded" height="100%" width="100%"/>
          </div>
        </swiper-slide>

      </swiper>
    </div>

    <div class="w-full mx-auto" style="flex: 1 1 0; max-width: 400px">
      <div v-show="activeIndex < images.length" class="relative">
        <swiper :modules="[SwiperFreeMode, SwiperNavigation, SwiperThumbs]" :spaceBetween="10"
                :thumbs="{ swiper: thumbsSwiper }" @slideChange="onSlideChange" @swiper="setMainSwiper">

          <swiper-slide v-for="(image, index) in combineImages" :key="`gallery-image-${index}`"
                        class="!flex justify-center">
            <single-image-with-zoom v-if="index === activeIndex" :image="image"/>
          </swiper-slide>

        </swiper>
      </div>

      <product-three-sixty-degree v-if="isOutOfBounds && !!threeSixtyImages?.length"
                                  :threeSixtyImages="threeSixtyImages"/>
      <image-comparison v-if="isBeforeAfterAvailable" :imageOne="beforeAfterImage?.before"
                        :imageTwo="beforeAfterImage?.after"/>
      <Video v-if="isVideoOpen" :videoUrl="data?.video_url" height="400"/>
    </div>
  </div>
</template>

<script setup>
import SingleImageWithZoom from "@/components/product/SingleImageWithZoom";
import ImageComparison from "@/components/product/ImageComparison";
import ProductThreeSixtyDegree from "@/components/ProductThreeSixtyDegree";
import Video from "@/components/product/Video";
import {EVENTS} from "@/constants/events";

const props = defineProps({
  data: {
    type: Object,
    default: {}
  },
  images: {
    type: Array,
    default: () => [],
  }
});

const extraImages = ref(null)
const threeSixtyImages = computed(() => props?.data?.images360)
const isOutOfBounds = computed(() => activeIndex.value >= props?.images?.length);

// before and after for image compare
const beforeAfterImage = computed(() => !!props?.data?.images_after_before ? props?.data?.images_after_before[0] : {})
const isBeforeAfterAvailable = computed(() => isOutOfBounds.value && beforeAfterImage.value)

// video slider
const sliderVideo = computed(() => props?.data?.video_url)
const sliderVideoThumbnail = computed(() => sliderVideo.value ? props?.images[0] : '')
const isVideoOpen = computed(() => isOutOfBounds.value && !!sliderVideo.value)

const combineImages = computed(() => {
  extraImages.value = threeSixtyImages.value[0] || beforeAfterImage.value?.before || sliderVideoThumbnail.value;
  return props?.images?.length ? [...props?.images, extraImages.value].filter(Boolean) : [];
});

const nextRef = ref(null);
const prevRef = ref(null);

const activeIndex = ref(0);
const thumbsSwiper = ref(null);

const onSlideChange = (e) => {
  activeIndex.value = e?.activeIndex;
};

const swiperInit = ref(false)
const setThumbsSwiper = (swiper) => {
  thumbsSwiper.value = swiper
  swiperInit.value = true
};

// Apply style when javascript is disabled
const getInitialStyle = computed(() => {
  if (swiperInit.value) return {}

  return {height: 'calc(100% / 3)'}
})

const mainSwiper = ref(null);
const setMainSwiper = (swiper) => mainSwiper.value = swiper

const isThreeSixtyActive = ref(false);
const isComparisonActive = ref(false);
const isVideoActive = ref(false);

const toggleState = (stateRef) => {
  stateRef.value = !stateRef.value;
  stateRef.value ? swapSwiperIndex() : resetSwiper();
};

const {on: openThreeSixtyPopup} = useEventBus(EVENTS.THREE_SIXTY_POPUP);
openThreeSixtyPopup(() => toggleState(isThreeSixtyActive));

const {on: onOpenCompareImage} = useEventBus(EVENTS.COMPARE_POPUP);
onOpenCompareImage(() => toggleState(isComparisonActive));

const {on: onOpenVideo} = useEventBus(EVENTS.PDP_VIDEO);
onOpenVideo(() => toggleState(isVideoActive));

const swapSwiperIndex = () => {
  let index = combineImages.value.length - 1
  thumbsSwiper.value?.slideTo(index);
  mainSwiper.value?.slideTo(index);
  activeIndex.value = index
}

const resetSwiper = () => {
  thumbsSwiper.value?.slideTo(0);
  mainSwiper.value?.slideTo(0);
  activeIndex.value = 0

  //update swiper 
  thumbsSwiper.value?.update();
  mainSwiper.value?.update();
}
</script>

<style>
// add height and width for firefox browser
.gallery .swiper {
  height: 100%;
  width: 100%;
}

.gallery-thumb .swiper-wrapper {
  display: flex;
  flex-direction: column;
}

.gallery-thumb .swiper-slide img {
  border: 1px solid #e5e7eb;
}

.gallery-thumb .swiper-slide-thumb-active img {
  border: 1px solid #222;
}

.three-sixty-preview,
.image-comparison,
.video-play-tag {
  z-index: 5;
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}

.three-sixty-preview::before,
.image-comparison::before,
.video-play-tag::before {
  content: "";
  position: absolute;
  inset: 0;
  background-size: 55%;
}

.three-sixty-preview::before {
  background: url("/images/360degree.svg") no-repeat center;
}

.image-comparison::before {
  background: url("/images/comparisonOverlayIcon.svg") no-repeat center;
  background-size: 60%;
}

.video-play-tag::before {
  background: url("/images/vouchers/audio-play.svg") no-repeat center;
}
</style>
